import { useEffect, useRef, useState } from 'react'

// Per-row "seconds" input for playlist / layout content rows. Styled to match
// the master-volume percentage field in the screen Settings (Audio card):
// rounded-lg, h-9, centered tabular-nums, spin-buttons hidden, focus ring.
//
// Unlike a raw <input type="number">, this keeps a local *string* draft while
// the field is focused so the operator can clear it and type a fresh value
// (e.g. backspace "10" → "" → "25") without it snapping back to 1 on every
// keystroke. The value is clamped to [min, max] and committed on blur / Enter.
//
// The row it lives in is draggable, so every pointer/keyboard event is stopped
// from bubbling and draggable is disabled on the input itself.
export default function DurationInput({
  value,
  onCommit,
  min = 1,
  max = 3600,
  suffix = 's',
  ariaLabel,
}: {
  value:     number
  onCommit:  (seconds: number) => void
  min?:      number
  max?:      number
  suffix?:   string
  ariaLabel?: string
}) {
  const [draft, setDraft] = useState(String(value))
  const focusedRef = useRef(false)

  // Keep the draft in sync with the persisted value when it changes upstream
  // (reorder, remove, or a parent re-fetch) — but never yank the field out from
  // under the operator while they're mid-edit.
  useEffect(() => {
    if (!focusedRef.current) setDraft(String(value))
  }, [value])

  const clamp = (n: number) => Math.max(min, Math.min(max, n))

  const commit = () => {
    focusedRef.current = false
    const parsed = parseInt(draft, 10)
    const next = Number.isFinite(parsed) ? clamp(parsed) : value
    setDraft(String(next))
    if (next !== value) onCommit(next)
  }

  return (
    <div className="flex items-center gap-1 shrink-0">
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft}
        aria-label={ariaLabel}
        draggable={false}
        onFocus={() => { focusedRef.current = true }}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur() }
          e.stopPropagation()
        }}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        className="w-14 h-9 rounded-lg border border-border bg-background px-2 text-sm text-center tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
      />
      <span className="text-sm text-muted-foreground">{suffix}</span>
    </div>
  )
}
